import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@bookdental/ui";

type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Botão de confirmação em vermelho (ações irreversíveis). */
  destructive?: boolean;
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setState(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (answer: boolean) => {
    resolverRef.current?.(answer);
    resolverRef.current = null;
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => !o && close(false)}>
        {state && (
          <DialogContent
            className="max-w-[320px] p-4"
            overlayClassName="backdrop-blur-none"
          >
            <div className="flex items-start gap-2.5">
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  state.destructive
                    ? "bg-red-100 text-red-600"
                    : "bg-brand-100 text-brand-600"
                }`}
              >
                {state.destructive ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : (
                  <HelpCircle className="h-3.5 w-3.5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-sm font-semibold text-zinc-900">
                  {state.title}
                </DialogTitle>
                {state.description && (
                  <div className="mt-1 space-y-0.5 text-xs leading-relaxed text-zinc-600">
                    {state.description}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="inline-flex h-8 flex-1 items-center justify-center rounded-md border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50"
              >
                {state.cancelText ?? "Cancelar"}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                className={`inline-flex h-8 flex-1 items-center justify-center rounded-md px-3 text-xs font-semibold text-white shadow-sm transition-colors ${
                  state.destructive
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-brand-600 hover:bg-brand-700"
                }`}
              >
                {state.confirmText ?? "Confirmar"}
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm precisa de ConfirmProvider");
  return ctx;
}
